import React, { memo, useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/Modal/modal';
import { Grid, TextField } from '@material-ui/core';
import Moment from 'moment/moment';
import { safeGet } from '../../../axios-default';
import LoadingSpinner from '../../../components/Loader/LoadingSpinner';
import { smilDesignQuestionsConfig, smilDesignQuestionsConfigFirstFit } from '../SmileDesignQuestions';
import { useSelector } from 'react-redux';
import MockupFeedbackStep from '../../RestorativePhaseCaseIntake/MockupFeedbackStep';
import PrescriptionNoteStep from '../../RestorativePhaseCaseIntake/PrescriptionNoteStep';
import ImpressionStep from '../../RestorativePhaseCaseIntake/ImpressionStep';
import { useLang } from '../../../util/lang';
import { restorationFormQuestion } from './RestorationFormQuestion';
import { isEmpty } from '../../../util/function';
import ImpressionViewer from '../../../components/PatientInfo/ImpressionViewer';

/** Loads a restoration form linked from a Case Log item. */
const getRestorationFormData = async (caseId, restorationFormId) => {
    const results = await safeGet(`/case/restorationForm/${caseId}/${restorationFormId}`);
    return results;
};

const getFlattenedCaseImpressions = (caseImpressions) =>
    (Array.isArray(caseImpressions) ? caseImpressions : [caseImpressions])
        .flat()
        .filter(Boolean)
        .sort((a, b) => new Date(b.created_date || 0) - new Date(a.created_date || 0));

const normalizeImpressionType = (impression = {}) => {
    const type = String(impression.type || '').toUpperCase();
    if (['COURIER', 'SCANNER', 'FILE'].includes(type)) return type;
    if (impression.modern_code || impression.waybill_number || impression.courier_company) return 'COURIER';
    if (impression.scanner_brand || impression.doctor_id) return 'SCANNER';
    return 'FILE';
};

const hasViewableImpression = (impression = {}) => {
    const type = normalizeImpressionType(impression);
    if (type === 'COURIER') {
        return Boolean(impression.modern_code || impression.waybill_number || impression.courier_company);
    }
    if (type === 'SCANNER') {
        return Boolean(impression.scanner_brand || impression.doctor_id);
    }
    return Boolean(impression.stl_file0 || impression.stl_file1 || impression.stl_file2 || impression.stl_file3);
};

const resolveViewImpression = (responseData, caseImpressions, caseImpressionId) => {
    const responseImpression =
        responseData?.caseImpression ||
        responseData?.case_impression ||
        responseData?.restorationForm?.caseImpression ||
        responseData?.restorationForm?.case_impression;
    if (hasViewableImpression(responseImpression)) return responseImpression;

    const flattenedCaseImpressions = getFlattenedCaseImpressions(caseImpressions);
    const matchedImpression = flattenedCaseImpressions.find(
        (impression) => String(impression.case_impression_id) === String(caseImpressionId)
    );

    return hasViewableImpression(matchedImpression)
        ? matchedImpression
        : flattenedCaseImpressions.find(hasViewableImpression) || responseImpression || matchedImpression || {};
};

const MOCKUP_STL_FIELDS = ['mockupUpperStl', 'mockupLowerStl', 'mockupBiteStl'];
const MOCKUP_FILE_ID_TO_FIELD = {
    mockup_upper: 'mockupUpperStl',
    mockup_lower: 'mockupLowerStl',
    mockup_bite: 'mockupBiteStl',
};
const MOCKUP_DETAIL_KEYS = [
    ['impressionType', 'type'],
    ['scannerBrand', 'scanner_brand'],
    ['doctorId', 'doctor_id'],
    ['modernCode', 'modern_code'],
    ['waybillNumber', 'waybill_number'],
    ['courierCompany', 'courier_company'],
];

const toSnakeCase = (key) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const getStoredText = (value) => (typeof value === 'string' ? value.trim() : '');

const getStoredFilePath = (value) => {
    if (typeof value === 'string') return value.startsWith('$$FILE:') ? '' : value.trim();
    if (value && typeof value === 'object') {
        return (
            [value.dataURL, value.url, value.data].find(
                (item) => typeof item === 'string' && !item.startsWith('$$FILE:') && item.trim()
            ) || ''
        );
    }
    return '';
};

const getMockupScans = (source) => {
    if (!source || typeof source !== 'object') return {};
    const scans = {};
    MOCKUP_STL_FIELDS.forEach((field) => {
        const storedValue = getStoredFilePath(source[field]) || getStoredFilePath(source[toSnakeCase(field)]);
        if (storedValue) scans[field] = storedValue;
    });
    [...(source.files || []), ...(source.restorativeImpression?.mockup?.files || [])].forEach((file) => {
        const field = MOCKUP_FILE_ID_TO_FIELD[file?.id];
        const storedValue = field && !scans[field] ? getStoredFilePath(file) : '';
        if (storedValue) scans[field] = storedValue;
    });
    return scans;
};

const resolveMockupScans = (responseData, caseImpressions, caseImpressionId) => {
    const restorationForm = responseData?.restorationForm || {};
    const flattenedCaseImpressions = getFlattenedCaseImpressions(caseImpressions);
    const matchedImpression = flattenedCaseImpressions.find(
        (impression) => String(impression.case_impression_id) === String(caseImpressionId)
    );
    const sources = [
        responseData,
        responseData?.caseImpression,
        responseData?.case_impression,
        responseData?.mockupFeedbackFiles,
        responseData?.mockup_feedback_files,
        restorationForm,
        restorationForm.caseImpression,
        restorationForm.case_impression,
        restorationForm.mockupFeedbackFiles,
        restorationForm.mockup_feedback_files,
        matchedImpression,
        ...flattenedCaseImpressions,
    ];
    return sources.reduce((scans, source) => {
        const sourceScans = getMockupScans(source);
        MOCKUP_STL_FIELDS.forEach((field) => {
            if (!scans[field] && sourceScans[field]) scans[field] = sourceScans[field];
        });
        return scans;
    }, {});
};

const collectMockupSources = (value, depth = 0) => {
    if (depth > 4 || !value || typeof value !== 'object') return [];
    return Object.entries(value).reduce((sources, [key, child]) => {
        if (!child || typeof child !== 'object') return sources;
        const nestedSources = collectMockupSources(child, depth + 1);
        return /mockup/i.test(key) && !Array.isArray(child)
            ? [...sources, child, ...nestedSources]
            : [...sources, ...nestedSources];
    }, []);
};

const toMockupImpressionSection = (source) => {
    if (!source || typeof source !== 'object') return null;
    const section = {};
    MOCKUP_DETAIL_KEYS.forEach(([formKey, impressionKey]) => {
        const value =
            getStoredText(source[formKey]) ||
            getStoredText(source[toSnakeCase(formKey)]) ||
            getStoredText(source[impressionKey]);
        if (value) section[impressionKey] = value;
    });
    MOCKUP_STL_FIELDS.forEach((field, index) => {
        const value =
            getStoredFilePath(source[field]) ||
            getStoredFilePath(source[toSnakeCase(field)]) ||
            getStoredFilePath(source[`stl_file${index}`]);
        if (value) section[`stl_file${index}`] = value;
    });
    (source.files || []).forEach((file) => {
        const index = MOCKUP_STL_FIELDS.indexOf(MOCKUP_FILE_ID_TO_FIELD[file?.id]);
        const value = index >= 0 && !section[`stl_file${index}`] ? getStoredFilePath(file) : '';
        if (value) section[`stl_file${index}`] = value;
    });
    return Object.keys(section).length ? section : null;
};

const isToothShapeImpression = (impression, caseImpressionId, toothShapeImpression) => {
    if (impression === toothShapeImpression) return true;
    return [caseImpressionId, toothShapeImpression?.case_impression_id]
        .filter((id) => id !== null && id !== undefined)
        .some((id) => String(impression?.case_impression_id) === String(id));
};

const resolveSiblingImpression = (caseImpressions, caseImpressionId, toothShapeImpression) => {
    const toothShapeCreatedDate = new Date(toothShapeImpression?.created_date || 0).getTime();
    if (!toothShapeCreatedDate) return null;
    const siblingImpression = getFlattenedCaseImpressions(caseImpressions)
        .filter(
            (impression) =>
                !isToothShapeImpression(impression, caseImpressionId, toothShapeImpression) &&
                hasViewableImpression(impression) &&
                new Date(impression.created_date || 0).getTime() >= toothShapeCreatedDate
        )
        .sort((a, b) => new Date(a.created_date || 0) - new Date(b.created_date || 0))[0];
    return siblingImpression || null;
};

const resolveMockupImpression = (responseData, caseImpressions, caseImpressionId, toothShapeImpression) => {
    const restorationForm = responseData?.restorationForm || {};
    const candidates = [
        restorationForm.restorativeImpression?.mockup,
        restorationForm.restorative_impression?.mockup,
        ...collectMockupSources(responseData),
        resolveMockupScans(responseData, caseImpressions, caseImpressionId),
        resolveSiblingImpression(caseImpressions, caseImpressionId, toothShapeImpression),
    ];
    const impression = candidates.reduce((merged, candidate) => {
        const section = toMockupImpressionSection(candidate);
        if (!section) return merged;
        Object.entries(section).forEach(([key, value]) => {
            if (!merged[key]) merged[key] = value;
        });
        return merged;
    }, { stl_files_later: false });
    const isViewable = hasViewableImpression(impression);
    if (!isViewable && responseData) {
        console.warn('[View Impressions] Mock-up adjustments data not found in the response', {
            responseKeys: Object.keys(responseData),
            restorationFormKeys: Object.keys(responseData.restorationForm || {}),
            mockupSources: collectMockupSources(responseData).length,
            caseImpressions: getFlattenedCaseImpressions(caseImpressions).length,
        });
    }
    return isViewable ? impression : null;
};

const partialTranslation = (ans, get) => {
    if (ans !== undefined && ans !== null && ans !== '' && (ans.startsWith('@') || ans.startsWith('option'))) {
        return get(`@PatientInfo.smileDesign.prescriptionNotes.${ans}`);
    }
    return ans;
};

const hasFirstFitRestorationForm = (restorationForm = {}) => Boolean(restorationForm.plannedRestorationSolution);

const LegacyRestorationFormContent = ({ data, get }) => {
    const restorationForm = data?.restorationForm || {};
    const restorationFormConfig = restorationFormQuestion(undefined, restorationForm.isMarieGroup);
    const toolsToOrder = Array.isArray(restorationForm.toolsToOrder) ? restorationForm.toolsToOrder : [];

    return (
        <>
            <div style={{ display: 'block', width: '100%' }}>
                <p style={{ display: 'inline', marginRight: '15px' }}>
                    <strong>{get('@PatientInfo.modals.uploadedAt')}</strong> :
                    {restorationForm.created_date
                        ? Moment(restorationForm.created_date).format('YYYY-MM-DD HH:mm')
                        : '-'}
                </p>
                <hr />
            </div>
            <div style={{ marginTop: 16 }}>
                <div>{get(restorationFormConfig[1].question)}</div>
                {toolsToOrder.map((v, i) => {
                    return (
                        <li key={i} style={{ fontWeight: 'bold', paddingLeft: 16 }}>
                            {partialTranslation(v, get)}
                        </li>
                    );
                })}
            </div>

            <div style={{ marginTop: 8 }}>
                <div>{get(restorationFormConfig[2].question)}</div>
                <div style={{ fontWeight: 'bold', paddingLeft: 16 }}>
                    <TextField
                        value={restorationForm.restorationRemark || ''}
                        style={{ width: '100%', marginTop: 4 }}
                        multiline={true}
                        minRows={4}
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </div>
            </div>
            {!isEmpty(data?.caseImpression) && (
                <div style={{ marginTop: 24 }}>
                    <Grid item xs={12} sm={12} md={12} style={{ padding: 8, textAlign: 'left' }}>
                        <ImpressionViewer caseImpression={data.caseImpression} />
                    </Grid>
                </div>
            )}
        </>
    );
};

/** Renders the requested restorative phase content as a read-only Case Log modal. */
const ViewRestorationFormFirstFitModal = ({
    open,
    handleClose,
    caseId,
    restorationFormId,
    caseImpressionId,
    viewType,
    hasMockupOrder,
    caseImpressions,
}) => {
    const get = useLang();
    const [data, setData] = useState(null);
    const [isLoading, setIsloading] = useState(true);
    const restorationForm = data?.restorationForm || {};
    const displayFirstFitFlag = useSelector((state) => state.globalReducer.displayFirstFitFlag);
    const shouldUseFirstFitQuestions = hasFirstFitRestorationForm(restorationForm) || displayFirstFitFlag;
    const q1Options = useMemo(
        () =>
            (shouldUseFirstFitQuestions ? smilDesignQuestionsConfigFirstFit : smilDesignQuestionsConfig)(
                undefined,
                restorationForm.isMarieGroup
            )[1].option,
        [shouldUseFirstFitQuestions, restorationForm.isMarieGroup]
    );

    useEffect(() => {
        if (!open || caseId === undefined || restorationFormId === undefined) return;

        /** Loads the selected restorative phase form for read-only display. */
        async function fetchData() {
            setIsloading(true);
            const response = await getRestorationFormData(caseId, restorationFormId);
            setData(response.result);
            setIsloading(false);
        }

        fetchData();
    }, [caseId, restorationFormId, open]);

    /** Renders the Case Log's requested restorative phase section. */
    const renderContent = () => {
        if (viewType === 'mockupFeedback') return <MockupFeedbackStep readOnly data={restorationForm} />;
        if (viewType === 'impression') {
            const toothShapeImpression = resolveViewImpression(data, caseImpressions, caseImpressionId);
            return (
                <ImpressionStep
                    readOnly
                    data={toothShapeImpression}
                    mockupImpression={resolveMockupImpression(
                        data,
                        caseImpressions,
                        caseImpressionId,
                        toothShapeImpression
                    )}
                    hasMockupOrder={hasMockupOrder}
                />
            );
        }
        if (!hasFirstFitRestorationForm(restorationForm)) return <LegacyRestorationFormContent data={data} get={get} />;
        return <PrescriptionNoteStep readOnly data={restorationForm} q1Options={q1Options} />;
    };

    const title =
        {
            mockupFeedback: 'Mock-up Feedback',
            impression: 'Impressions',
            prescription: get('@PatientInfo.smileDesign.viewRestorationPhaseOrder'),
        }[viewType] || get('@PatientInfo.smileDesign.viewRestorationPhaseOrder');

    return (
        <Modal
            className="prescriptionNotes"
            modal="ViewRestorationFormFirstFitModal"
            title={title}
            handleClose={handleClose}
            open={open}
            submitButton={false}
            modalError={false}
        >
            {isLoading ? <LoadingSpinner /> : renderContent()}
        </Modal>
    );
};

export default memo(ViewRestorationFormFirstFitModal);
